import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

export type DashboardView = 'panel' | 'mis-espacios' | 'alquileres' | 'mis-reservas' | 'ajustes' | 'facturas';
// NOTE: 'ajustes' and 'facturas' remain in this union for the sidebar's
// `navigateTo` switch, but they are real routes (/ajustes, /facturas) —
// not `DashboardComponent` sub-views.

/**
 * Owner dashboard router commands (authenticated shell).
 * Pass directly to `router.navigate(OWNER_ROUTES.panel)` — do not wrap in another array.
 */
export const OWNER_ROUTES = {
  panel: ['/dashboard'] as const,
  spaces: ['/mis-espacios'] as const,
  spaceEdit: (spaceId: string) => ['/mis-espacios', spaceId, 'editar'] as const,
  spaceCalendar: (spaceId: string) => ['/espacio', spaceId, 'calendario'] as const,
  manageBookings: ['/gestionar-reservas'] as const,
} as const;

/**
 * Shared navigation state between MenuComponent (sidebar)
 * and DashboardComponent (content rendering).
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private document = inject(DOCUMENT);

  readonly activeView = signal<DashboardView>('panel');
  readonly navTick    = signal(0);

  setView(view: DashboardView): void {
    this.activeView.set(view);
    this.navTick.update(v => v + 1);
    this.document.defaultView?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** Maps authenticated app URLs to dashboard tabs. */
  viewForUrl(url: string): DashboardView | null {
    const path = url.split('?')[0];
    if (path.startsWith('/mis-espacios') || (path.includes('/espacio/') && path.includes('/calendario'))) {
      return 'mis-espacios';
    }
    if (path.startsWith('/gestionar-reservas') || path.startsWith('/owner/manage-bookings')) {
      return null;
    }
    if (path.startsWith('/mis-reservas')) return 'mis-reservas';
    if (path === '/dashboard' || path === '/dashboard/') return 'panel';
    return null;
  }
}
